import { useEffect, useRef, useState, useCallback } from "react";

interface UseDebouncedAsyncOptions<TParams, TResponse> {
  fn: (params: TParams) => Promise<TResponse>;
  delay?: number;
  params: TParams;
}

export function useDebouncedAsync<TParams, TResponse>({
  fn,
  delay = 800,
  params,
}: UseDebouncedAsyncOptions<TParams, TResponse>) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TResponse | null>(null);
  const [error, setError] = useState<any>(null);

  const execute = useCallback(
    async (currentParams: TParams) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(async () => {
        try {
          setLoading(true);
          setError(null);

          const response = await fn(currentParams);
          setData(response);
        } catch (err) {
          setError(err);
        } finally {
          setLoading(false);
        }
      }, delay);
    },
    [fn, delay]
  );

  useEffect(() => {
    // 🚫 Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    execute(params);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [params]);

  return { loading, data, error };
}
